import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { HubAlertList, HubAlertShimmer } from './HubAlertList';
import { hubAlertCount, hubAlertRows, type HubAlertRow } from './hubAlerts';
import { useGiftGuideHubLink } from './giftGuideHubFlag';
import { signOutUser } from '../auth/accountAuth';
import { useAuth } from '../firebase/FirebaseContext';
import { useAlerts, type Alert } from '../hooks/useAlerts';
import { useAuthSnapshot } from '../hooks/useAuthSnapshot';
import { useAllSavedActivities } from '../hooks/useAllSavedActivities';
import { useRecipients } from '../hooks/useRecipients';
import { gaMenuOpened } from '../lib/gaPixel';
import { markSavedNativeBackSafe } from '../lib/savedBackMarker';
import { shimmerFill } from '../../motion';

/**
 * The Gift Guide Hub's DESKTOP avatar menu (THE-967 PR 1).
 *
 * Mounted ONLY by `HubNavigationFrame`, in place of `HeaderAccountMenu`, and
 * only once that frame's `useGiftGuideHubGate()` has resolved `enabled`. With
 * the switch off or pending the frame never renders, so every one of
 * `HeaderAccountMenu`'s ~10 non-Hub mount points (SiteHeader's default actions,
 * PublicBoardAccountSlot, BoardHeaderActions, RecommendationResultsPage's
 * flag-off path, the marketing pages) keeps today's menu untouched. That is the
 * PRD's "every page with the Hub switch off" rule, and it is enforced
 * structurally — `HeaderAccountMenu.tsx` is not edited at all — rather than by a
 * flag check this file would have to get right ten times.
 * `hub/__tests__/HubAccountMenu.test.tsx` pins the single-importer census.
 *
 * SIGNED-IN ONLY. The Hub frame hands this component its account slot only when
 * `useAuthSnapshot(auth).isPermanent`; a guest gets `HubLoginButton` instead. So
 * unlike `HeaderAccountMenu` there is no anon avatar, no sign-in pill, no
 * save-boards nudge and no auth-bootstrap placeholder here — those states belong
 * to the frame's other branch.
 *
 * SPLIT INTO FOUR, each layer with one job:
 *   - `HubAccountMenuView`   pure, every value a prop. Deterministic in tests.
 *   - `HubAccountMenuPanel`  wired saves data. Mounted ONLY while the menu is
 *                            open, so the per-recipient Firestore fan-out never
 *                            spins up for someone who never opens the menu
 *                            (same stance `V4AccountDropdown` documents).
 *   - `HubAccountMenuMount`  the avatar, the open/focus state, and the alert
 *                            subscription — which must be always-on, because
 *                            the avatar NUMBER is a function of it.
 *   - `HubAccountMenu`       reads the account and keys the whole thing by uid.
 *
 * THE COUNT INVARIANT. `hubAlertRows()` (`hub/hubAlerts.ts`, the SAME row
 * model the mobile You tab and its tab-bar dot read) is called exactly once per
 * render, in `HubAccountMenuMount`, and the avatar number is `hubAlertCount()`
 * over that same array — the array the rows map over. There is no second
 * filter and no second count, on this surface or across surfaces, so "the
 * number equals the rows shown" and "desktop agrees with mobile" are both true
 * by construction rather than by code paths agreeing.
 *
 * The "Confirm your email" row IS counted. An earlier revision of this file
 * argued it should not be — a gating affordance with no dismiss is not a
 * notification — but Kate ruled otherwise on THE-971's pre-merge review
 * (2026-10-15): count == rows shown, literally, including that row. The
 * previous behaviour made the badge read one less than the list whenever the
 * email was unconfirmed.
 *
 * SWITCHING ACCOUNTS. `HubAccountMenu` renders `HubAccountMenuMount` with
 * `key={uid}`, so a uid change REMOUNTS the entire subtree: alerts, saves,
 * recipients, open state and the rendered name/email all restart from empty in
 * the same commit the uid changes. The underlying hooks do each reset
 * themselves on `onAuthStateChanged`, but they reset in their own effects a
 * beat later — which is the window where the previous account's name and
 * notifications linger, exactly the defect the PRD calls out. Keying closes
 * the window instead of racing it.
 */

/**
 * How long a still-loading section waits before offering "Couldn't load.
 * Retry" — the PRD's "after a few seconds". Long enough that a normal cold
 * Firestore read never trips it, short enough to beat a user's patience.
 */
export const HUB_ACCOUNT_SLOW_LOAD_MS = 5000;

/** Where the Hub's Settings row goes. The route itself ships in THE-967 PR 2
 *  (thea-web #757); this is the Hub's single sanctioned spelling of it so PR 3
 *  and the Settings page cannot drift onto a different path. */
export const HUB_SETTINGS_PATH = '/settings';

/** The all-saves board — the same destination the You tab's saves row and
 *  `V4AccountDropdown`'s saves row use. */
export const HUB_ALL_SAVES_PATH = '/liked';

const Wrapper = styled.div`
  position: relative;
`;

const Avatar = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  border: 0;
  background: ${({ theme }) => theme.color.clay};
  color: #ffffff;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

/* The avatar NUMBER, not a dot. Desktop shows the count (the mobile You tab's
   dot is PR 3's); `HeaderAccountMenu`'s `ThresholdDot` is a dot because it
   stands for several unrelated causes at once, whereas this stands for exactly
   the notification rows below it. */
const AvatarCount = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  box-sizing: border-box;
  border-radius: 9999px;
  background: #d93025;
  border: 2px solid #ffffff;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  pointer-events: none;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 288px;
  display: flex;
  flex-direction: column;
  /* Internal scroll rather than growth: the menu must never run off the
     viewport and take Settings / Log out with it. The cap leaves room for the
     Hub header above and a margin below. */
  max-height: min(560px, calc(100dvh - 108px));
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  font-family: ${({ theme }) => theme.font.sans};
  text-align: left;
  z-index: 60;
`;

/* Everything that can grow without bound lives here and scrolls. */
const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px 12px 4px;
`;

/* PINNED. Settings and Log out sit outside the scroller, so they are reachable
   at any scroll position and at any viewport height — the PRD's "Settings and
   Log out always reachable". They are also outside the notifications/saves
   region, which is why they keep working while that region is in its error
   state. */
const Footer = styled.div`
  flex: 0 0 auto;
  padding: 4px 12px 12px;
  background: #ffffff;
  border-radius: 0 0 12px 12px;
`;

const Identity = styled.div`
  padding: 4px 8px 12px;
  .name {
    font-size: 14px;
    font-weight: 600;
    color: hsl(var(--foreground));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .email {
    margin-top: 2px;
    font-size: 12px;
    color: ${({ theme }) => theme.color.mutedText};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SectionLabel = styled.div`
  padding: 0 8px;
  margin: 2px 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.mutedText};
`;

const Divider = styled.div`
  height: 1px;
  margin: 8px 0;
  background: ${({ theme }) => theme.color.warmBorder};
`;

const Quiet = styled.div`
  padding: 2px 8px 10px;
  font-size: 13.5px;
  color: ${({ theme }) => theme.color.mutedText};
`;

const rowBase = `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  text-decoration: none;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
`;

const SavedRow = styled.a`
  ${rowBase};
  color: hsl(var(--foreground));
  &:hover { background: ${({ theme }) => theme.color.creamLight}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 1px;
  }
  .chev { color: ${({ theme }) => theme.color.mutedText}; font-size: 16px; line-height: 1; }
`;

const SettingsRow = styled.a`
  ${rowBase};
  color: hsl(var(--foreground));
  font-weight: 500;
  &:hover { background: ${({ theme }) => theme.color.creamLight}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 1px;
  }
  .left { display: inline-flex; align-items: center; gap: 10px; }
  .chev { color: ${({ theme }) => theme.color.mutedText}; font-size: 16px; line-height: 1; }
`;

/* "Confirm your email": no ✕. The PRD is explicit that this row is not
   dismissable — it is the gate, and a dismissed gate is an account stuck in the
   waiting stage with no way back to it. */
const ConfirmRow = styled.button`
  ${rowBase};
  background: ${({ theme }) => theme.color.creamLight};
  color: ${({ theme }) => theme.color.clay};
  font-weight: 600;
  margin-bottom: 4px;
  &:hover { background: ${({ theme }) => theme.color.cream}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 1px;
  }
  .chev { font-size: 16px; line-height: 1; }
`;

/* Log out ALONE in grey — subordinate on purpose, per the PRD's menu
   inventory: it is the one destructive-ish action and should not compete with
   Settings for the eye. */
const LogOutRow = styled.button`
  ${rowBase};
  color: ${({ theme }) => theme.color.mutedText};
  &:hover { background: ${({ theme }) => theme.color.creamLight}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 1px;
  }
`;

const RetryBox = styled.div`
  padding: 6px 8px 10px;
  font-size: 13.5px;
  color: hsl(var(--foreground));
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RetryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: #ffffff;
  color: ${({ theme }) => theme.color.clay};
  border-radius: 9999px;
  padding: 4px 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.color.creamLight}; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 1px;
  }
`;

const SavesShimmer = styled.span`
  ${shimmerFill};
  display: block;
  height: 14px;
  width: 64%;
  margin: 10px 8px 14px;
  border-radius: 7px;
`;

const GearIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

/** `loading` that has outstayed its welcome. Returns true once `loading` has
 *  been continuously true for `ms`, and false again the moment it clears or the
 *  caller re-arms it (a fresh `key`). */
function useSlowLoad(loading: boolean, ms: number): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!loading) {
      setSlow(false);
      return undefined;
    }
    const t = setTimeout(() => setSlow(true), ms);
    return () => clearTimeout(t);
  }, [loading, ms]);
  return slow;
}

/** One section's data state. `error` is "still not here after a few seconds",
 *  which is the only failure a Firestore listener actually shows a user — it
 *  retries forever rather than reporting a terminal error. */
export type HubSectionState = 'loading' | 'error' | 'ready';

export interface HubAccountMenuViewProps {
  displayName: string | null;
  email: string | null;
  /** The notification rows, from `hubAlertRows` — including the "Confirm your
   *  email" gate row when the account has an address still to confirm. */
  rows: readonly HubAlertRow[];
  alertsState: HubSectionState;
  onAlertClick: (alert: Alert) => void;
  onDismiss: (alertId: string) => void;
  savedCount: number;
  peopleCount: number;
  savesState: HubSectionState;
  savedHref: string;
  settingsHref: string;
  onLogOut: () => void;
  /** Re-arm the loaders (restarts the saves fan-out and the slow-load timer). */
  onRetry: () => void;
  /**
   * PR 2's `<EmailVerificationNotice user={user} hub />`, rendered inline when
   * the "Confirm your email" row is tapped. Optional: without it the row opens a
   * one-line interim slot pointing at Settings rather than faking the box.
   * `HubNavigationFrame` passes the real one.
   */
  confirmBox?: React.ReactNode;
  /** Close the menu. `returnFocus` moves focus back to the avatar. */
  onRequestClose: (returnFocus: boolean) => void;
  /** Focus the first item on mount (the menu was opened by keyboard). */
  autoFocusFirst: boolean;
  /** `id` of the avatar, for `aria-labelledby`. */
  labelledBy: string;
}

export const HubAccountMenuView: React.FC<HubAccountMenuViewProps> = ({
  displayName,
  email,
  rows,
  alertsState,
  onAlertClick,
  onDismiss,
  savedCount,
  peopleCount,
  savesState,
  savedHref,
  settingsHref,
  onLogOut,
  onRetry,
  confirmBox,
  onRequestClose,
  autoFocusFirst,
  labelledBy,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const needsEmailConfirm = rows.some((r) => r.kind === 'confirm-email');
  const alerts = useMemo(
    () => rows.flatMap((r) => (r.kind === 'alert' ? [r.alert] : [])),
    [rows],
  );

  // Same identity chain as YouPage / HeaderAccountMenu / V4AccountDropdown: the
  // email line only shows when the name line came from a real displayName,
  // otherwise it would just repeat the email.
  const name = displayName || email || 'Signed in';
  const showEmailLine = Boolean(displayName && email);

  const items = useCallback(
    () =>
      Array.from(
        rootRef.current?.querySelectorAll<HTMLElement>('[data-hub-menu-item]') ?? [],
      ),
    [],
  );

  useEffect(() => {
    if (!autoFocusFirst) return;
    items()[0]?.focus();
  }, [autoFocusFirst, items]);

  // Arrow-key walking is done over the LIVE DOM rather than an index of known
  // rows, because what is focusable here changes with the data (shimmer rows,
  // the retry button, the confirm row, N notification rows). Every focusable
  // item in this menu carries `data-hub-menu-item`; nothing else needs to know
  // the order, which is also why `HubAlertList` can own its own row markup.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Stop here: an Esc aimed at the menu must not also reach a host that
      // treats Esc as "leave this page".
      e.stopPropagation();
      onRequestClose(true);
      return;
    }
    if (e.key === 'Tab') {
      // Tabbing out of the menu closes it, but focus is already on its way
      // somewhere else — pulling it back to the avatar would fight the user.
      const list = items();
      const last = list[list.length - 1];
      const first = list[0];
      if ((!e.shiftKey && e.target === last) || (e.shiftKey && e.target === first)) {
        onRequestClose(false);
      }
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') {
      return;
    }
    const list = items();
    if (list.length === 0) return;
    e.preventDefault();
    const at = list.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home'
        ? 0
        : e.key === 'End'
          ? list.length - 1
          : e.key === 'ArrowDown'
            ? (at + 1) % list.length
            : at <= 0
              ? list.length - 1
              : at - 1;
    list[next]?.focus();
  };

  const savedLabel = `${savedCount} saved across ${peopleCount} ${
    peopleCount === 1 ? 'person' : 'people'
  }`;

  return (
    <Dropdown
      ref={rootRef}
      role="menu"
      aria-labelledby={labelledBy}
      data-testid="hub-account-menu"
      onKeyDown={handleKeyDown}
    >
      <Body>
        <Identity data-testid="hub-account-identity">
          <div className="name">{name}</div>
          {showEmailLine ? <div className="email">{email}</div> : null}
        </Identity>

        <SectionLabel>Notifications</SectionLabel>
        <div
          data-testid="hub-account-notifications"
          /* The count, as rendered. Same array the rows map over and the same
             number the avatar shows — asserted together, so the three can
             never be verified separately and drift. */
          data-hub-alert-count={hubAlertCount(rows)}
        >
          {needsEmailConfirm && (
            <>
              <ConfirmRow
                type="button"
                role="menuitem"
                data-hub-menu-item
                data-testid="hub-account-confirm-email"
                aria-expanded={confirmOpen}
                onClick={() => setConfirmOpen((v) => !v)}
              >
                <span>Confirm your email</span>
                <span className="chev" aria-hidden="true">
                  ›
                </span>
              </ConfirmRow>
              {confirmOpen && (
                <div data-testid="hub-account-confirm-box">
                  {confirmBox ?? (
                    <Quiet>
                      Check {email ?? 'your inbox'} for the confirmation link, or
                      finish it in Settings.
                    </Quiet>
                  )}
                </div>
              )}
            </>
          )}

          {alertsState === 'loading' && <HubAlertShimmer />}
          {alertsState === 'error' && (
            <RetryBox role="status" data-testid="hub-account-notifications-error">
              <span>Couldn&rsquo;t load.</span>
              <RetryButton
                type="button"
                data-hub-menu-item
                onClick={onRetry}
              >
                Retry
              </RetryButton>
            </RetryBox>
          )}
          {alertsState === 'ready' &&
            (alerts.length > 0 ? (
              <HubAlertList
                alerts={alerts}
                onAlertClick={(a) => {
                  onRequestClose(false);
                  onAlertClick(a);
                }}
                onDismiss={onDismiss}
              />
            ) : (
              /* Only ever reachable AFTER the listener answered — the loading
                 and error branches above own every other case, which is the
                 PRD's "never show You're all caught up before data arrives". */
              <Quiet data-testid="hub-account-notifications-empty">
                You&rsquo;re all caught up.
              </Quiet>
            ))}
        </div>

        {savesState === 'loading' && (
          <SavesShimmer data-testid="hub-account-saves-shimmer" aria-hidden="true" />
        )}
        {savesState === 'error' && (
          <RetryBox role="status" data-testid="hub-account-saves-error">
            <span>Couldn&rsquo;t load.</span>
            <RetryButton type="button" data-hub-menu-item onClick={onRetry}>
              Retry
            </RetryButton>
          </RetryBox>
        )}
        {savesState === 'ready' &&
          (savedCount > 0 ? (
            <SavedRow
              role="menuitem"
              data-hub-menu-item
              data-testid="hub-account-saves"
              href={savedHref}
              aria-label={savedLabel}
              /* Arms the short-lived "native back is safe" marker so /liked can
                 history.back() to here with its scroll instead of a fallback
                 landing. The helper ignores modified/middle clicks, a cancelled
                 click and any non-/liked destination, and self-expires. */
              onClick={markSavedNativeBackSafe}
            >
              <span>{savedLabel}</span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </SavedRow>
          ) : (
            <Quiet data-testid="hub-account-saves-empty">Nothing saved yet.</Quiet>
          ))}
      </Body>

      <Footer>
        <Divider />
        <SettingsRow
          role="menuitem"
          data-hub-menu-item
          data-testid="hub-account-settings"
          href={settingsHref}
        >
          <span className="left">
            <GearIcon />
            <span>Settings</span>
          </span>
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </SettingsRow>
        <Divider />
        <LogOutRow
          type="button"
          role="menuitem"
          data-hub-menu-item
          data-testid="hub-account-logout"
          onClick={onLogOut}
        >
          Log out
        </LogOutRow>
      </Footer>
    </Dropdown>
  );
};

interface HubAccountMenuPanelProps
  extends Omit<
    HubAccountMenuViewProps,
    'savedCount' | 'peopleCount' | 'savesState' | 'savedHref' | 'onRetry'
  > {
  savedHref: string;
  onRetry: () => void;
}

/**
 * The saves half of the menu's data, mounted only while the menu is open.
 *
 * `useAllSavedActivities` is the SAME dataset the all-saves board (`/liked`)
 * renders: SAVED plus PURCHASED-not-also-saved, across every recipient. That is
 * the PRD's requirement and it is NOT `useTotalLikedCount`, which the legacy
 * menu and `V4AccountDropdown` use — that hook filters `state == 'SAVED'`, so a
 * user who marked gifts bought saw the menu's number fall below the number of
 * cards `/liked` actually shows them.
 */
const HubAccountMenuPanel: React.FC<HubAccountMenuPanelProps> = ({
  alertsState,
  onRetry,
  ...rest
}) => {
  const { recipients, hydrated: recipientsHydrated } = useRecipients();
  const { activities, hydrated: activitiesHydrated } = useAllSavedActivities(recipients);

  // "across {P} people" means the people the saves are spread ACROSS, not the
  // size of the address book: a user with 12 recipients and saves for 3 of them
  // reads "9 saved across 12 people" as nine gifts thinly spread over twelve,
  // which is not what they have. Derived from the same `activities` array that
  // produced N, so the two halves of the sentence cannot disagree.
  const peopleCount = useMemo(
    () => new Set(activities.map((a) => a.recipientId)).size,
    [activities],
  );

  const loading = !recipientsHydrated || !activitiesHydrated;
  const slow = useSlowLoad(loading || alertsState === 'loading', HUB_ACCOUNT_SLOW_LOAD_MS);
  const savesState: HubSectionState = loading ? (slow ? 'error' : 'loading') : 'ready';

  return (
    <HubAccountMenuView
      {...rest}
      // One timer for the whole panel: a user does not read two independent
      // "Couldn't load" states, and Retry re-arms both.
      alertsState={alertsState === 'loading' && slow ? 'error' : alertsState}
      savedCount={activities.length}
      peopleCount={peopleCount}
      savesState={savesState}
      onRetry={onRetry}
    />
  );
};

export interface HubAccountMenuMountProps {
  /** The live account, already resolved by `HubAccountMenu`. */
  displayName: string | null;
  email: string | null;
  emailVerified: boolean;
  onLogOut: () => void;
  settingsHref: string;
  savedHref?: string;
  confirmBox?: React.ReactNode;
  /** Story/test hook: open the menu on mount. */
  defaultOpen?: boolean;
}

export const HubAccountMenuMount: React.FC<HubAccountMenuMountProps> = ({
  displayName,
  email,
  emailVerified,
  onLogOut,
  settingsHref,
  savedHref = HUB_ALL_SAVES_PATH,
  confirmBox,
  defaultOpen = false,
}) => {
  // ALWAYS subscribed, not deferred to open like the saves fan-out: the avatar
  // number is a function of this, so it has to be live before anyone opens
  // anything. Same reason `HeaderAccountMenu` subscribes its alerts eagerly.
  const { alerts, hydrated: alertsHydrated, dismiss } = useAlerts();
  const [open, setOpen] = useState(defaultOpen);
  const [autoFocusFirst, setAutoFocusFirst] = useState(false);
  // Bumping this re-keys the panel: fresh saves listeners and a fresh
  // slow-load timer. The ALERT listener is not re-created — Firestore's
  // onSnapshot retries by itself and keeps the last good list — so Retry's job
  // there is to re-arm the timer and give it another few seconds.
  const [retryNonce, setRetryNonce] = useState(0);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const avatarRef = useRef<HTMLButtonElement | null>(null);
  const avatarId = useId();

  // The same gate predicate the mobile You tab and its dot use
  // (`YouPage`, `useHubYouTabDot`): this menu only mounts for permanent
  // accounts, and an account with no address has nothing to confirm.
  const needsEmailConfirm = !emailVerified && !!email;
  const rows = useMemo(
    () => hubAlertRows({ alerts, needsEmailConfirm }),
    [alerts, needsEmailConfirm],
  );
  // Includes the "Confirm your email" row when it renders — Kate's ruling on
  // THE-971's pre-merge review: count == rows shown. One counter, in
  // `hubAlerts.ts`, shared with the mobile You tab and its dot (PR 3).
  const count = hubAlertCount(rows);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setAutoFocusFirst(false);
    if (returnFocus) avatarRef.current?.focus();
  }, []);

  // Outside click closes, and deliberately does NOT pull focus back to the
  // avatar: the user is already pointing somewhere else.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, close]);

  const label = count > 0 ? `Account menu — ${count} notifications` : 'Account menu';

  return (
    <Wrapper ref={wrapperRef}>
      <Avatar
        ref={avatarRef}
        id={avatarId}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        data-testid="hub-account-avatar"
        onClick={() => {
          if (!open) gaMenuOpened();
          setAutoFocusFirst(false);
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            // Open-and-enter, the standard menu-button gesture. Enter/Space
            // are the native button activation and go through onClick.
            e.preventDefault();
            if (!open) gaMenuOpened();
            setAutoFocusFirst(true);
            setOpen(true);
            return;
          }
          if (e.key === 'Escape' && open) {
            e.preventDefault();
            close(true);
          }
        }}
      >
        <span aria-hidden>{initialOf(displayName, email)}</span>
        {count > 0 && (
          <AvatarCount data-testid="hub-account-avatar-count" aria-hidden>
            {count}
          </AvatarCount>
        )}
      </Avatar>
      {open && (
        <HubAccountMenuPanel
          key={retryNonce}
          displayName={displayName}
          email={email}
          rows={rows}
          alertsState={alertsHydrated ? 'ready' : 'loading'}
          onAlertClick={(a) => {
            // Plain navigation rather than `useNavigate()`, matching the saves
            // and Settings anchors: these are cross-surface jumps and a full
            // load is fine.
            if (typeof window !== 'undefined') window.location.assign(a.href);
          }}
          onDismiss={dismiss}
          savedHref={savedHref}
          settingsHref={settingsHref}
          onLogOut={() => {
            close(false);
            onLogOut();
          }}
          onRetry={() => setRetryNonce((n) => n + 1)}
          confirmBox={confirmBox}
          onRequestClose={close}
          autoFocusFirst={autoFocusFirst}
          labelledBy={avatarId}
        />
      )}
    </Wrapper>
  );
};

function initialOf(displayName: string | null, email: string | null): string {
  const ch = (displayName || email || '').trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

export interface HubAccountMenuProps {
  /** PR 2's `<EmailVerificationNotice user={user} hub />`, if the host has it. */
  confirmBox?: React.ReactNode;
}

/**
 * The wired Hub avatar menu. Reads the account, resolves the Settings href
 * (carrying `?giftGuideHub=1` so a preview-cohort tester is not dropped), and
 * keys the whole subtree by uid so a switched account cannot linger.
 */
export const HubAccountMenu: React.FC<HubAccountMenuProps> = ({ confirmBox }) => {
  const auth = useAuth();
  // THE-972 / #757 added `emailVerified` to `useAuthSnapshot` for exactly this
  // read (it dedupes by VALUE, not by `User` reference, so a `reload()` +
  // `getIdToken(true)` flip of `emailVerified` on the same mutated `User`
  // object still commits) — the mobile You tab and its dot already read
  // identity there. `displayName`/`email` stay off `auth.currentUser` directly,
  // same as `YouPage`; `uid` drives this component's remount key below.
  const { uid, emailVerified } = useAuthSnapshot(auth);
  const displayName = auth.currentUser?.displayName ?? null;
  const email = auth.currentUser?.email ?? null;
  const carryHubParam = useGiftGuideHubLink();

  const handleLogOut = useCallback(async () => {
    try {
      await signOutUser(auth);
    } catch (err) {
      // Non-fatal: the auth listener still reconciles the signed-out state.
      console.error('[HubAccountMenu] signOut failed:', err);
    }
  }, [auth]);

  return (
    <HubAccountMenuMount
      key={uid ?? 'no-account'}
      displayName={displayName}
      email={email}
      emailVerified={emailVerified}
      settingsHref={carryHubParam(HUB_SETTINGS_PATH)}
      savedHref={carryHubParam(HUB_ALL_SAVES_PATH)}
      onLogOut={handleLogOut}
      confirmBox={confirmBox}
    />
  );
};

export default HubAccountMenu;
